import posthog from "posthog-js";
import type { AuthUser } from "../stores/useAuthStore";

let initialized = false;

// Mirrors App.tsx's isAuthRoute — the routes reachable before login. Kept
// as a separate, manually-synced list rather than a shared import because
// initPostHog() runs once at module load (main.tsx), before App.tsx's
// router exists; a static list is what PostHog's autocapture config
// actually needs anyway (it can't react to auth state at runtime). Update
// both places together if the set of pre-auth routes changes.
const UNAUTHENTICATED_ROUTES = [/\/sign-in/, /\/forgot-password/, /\/reset-password/, /\/invite\//];

/**
 * Initializes PostHog. No-ops (and PostHog stays uninitialized) when
 * VITE_POSTHOG_KEY isn't set — this is the single gate for whether any
 * analytics traffic leaves the browser, so a machine with no key configured
 * (e.g. a teammate who hasn't set up .env.development) sends nothing, while
 * one with a key (local test project or the real staging/prod project) does.
 */
export function initPostHog(): void {
  const key = import.meta.env.VITE_POSTHOG_KEY;
  if (!key) return;

  posthog.init(key, {
    api_host: import.meta.env.VITE_POSTHOG_HOST,
    // SPA route changes don't trigger a full page load, so PostHog's default
    // pageview autocapture misses them — pageviews are captured manually
    // (see usePostHogPageview) instead.
    capture_pageview: false,
    // Session replay — enabled per product's request. maskAllInputs is
    // already posthog-js's own default; set explicitly so the intent is
    // visible in code, not just implied by an SDK default that could
    // change. Revisit if specific on-screen elements (not just inputs)
    // need masking too (PostHog supports a `ph-mask` class for that).
    disable_session_recording: false,
    session_recording: {
      maskAllInputs: true,
    },
    // Autocapture (clicks, form submits) still fires regardless of
    // person_profiles — that setting only governs whether a Person profile
    // gets created, not whether events fire at all. Excluding the
    // pre-login routes here stops the remaining source of anonymous
    // noise: someone clicking around /sign-in without ever logging in.
    autocapture: {
      url_ignorelist: UNAUTHENTICATED_ROUTES,
    },
    // Without this, every anonymous pre-login visit (bouncing off /sign-in,
    // a bot, a fresh incognito window) creates its own full Person profile
    // and inflates Active Users / the Persons list with noise that was
    // never a real logged-in user. Verified empirically that
    // "identified_only" (the SDK's own default) does NOT prevent this in
    // practice — a genuinely fresh incognito window still produced a new
    // anonymous Person. "never" is stronger: it stops auto-captured events
    // (autocapture, pageview) from creating/attaching to a Person profile
    // at all. Explicit identify()/group() calls (see analytics.identify)
    // are a distinct code path from ordinary capture() and are expected to
    // still create/update a real Person when someone actually logs in —
    // confirm this holds after deploying, since PostHog's docs don't fully
    // spell out this interaction and it wasn't verified beyond reasoning
    // about the SDK's documented intent.
    person_profiles: "never",
  });
  initialized = true;
}

/**
 * All PostHog interaction call sites should go through these functions
 * rather than importing posthog-js directly — every call here is a no-op
 * until initPostHog() has actually initialized the SDK (i.e. a key was
 * configured), so features can call analytics.* unconditionally without
 * checking initialization state themselves.
 */
export const analytics = {
  capture(event: string, properties?: Record<string, unknown>): void {
    if (!initialized) return;
    posthog.capture(event, properties);
  },

  identify(user: AuthUser): void {
    if (!initialized) return;
    const distinctId = user.sub ?? user.email;
    if (!distinctId) return;

    posthog.identify(distinctId, {
      email: user.email,
      name: user.full_name ?? user.fullName,
      // Platform-level role (drives sidebar access control, see useAuthStore)
      // takes priority over the more general `role` field — it's the
      // authoritative "what kind of user is this" signal. Lets product
      // break down engagement insights by org (group) then by role
      // (Engineer vs. Operator vs. Org Admin) within it.
      role: user.platformRole ?? user.role,
    });
  },

  group(type: string, key: string | number, properties?: Record<string, unknown>): void {
    if (!initialized) return;
    posthog.group(type, String(key), properties);
  },

  reset(): void {
    if (!initialized) return;
    posthog.reset();
  },

  /**
   * Registers "super properties" — attached automatically to every event
   * captured from here on (manual capture() calls AND autocapture), not
   * just the call site that registered them. This is how user/org/facility
   * context ends up on every event without threading it through every
   * capture() call individually. Persisted in the browser until changed or
   * cleared by reset() (which clears registered properties along with the
   * rest of the identity, on logout).
   */
  register(properties: Record<string, unknown>): void {
    if (!initialized) return;
    posthog.register(properties);
  },
};
